<script setup lang="ts">
import useBackend from '@/composition/useBackend';
import {computed, ComputedRef, onMounted, ref} from 'vue';
import AddressEditor from '@/components/LegacyAddressEditor.vue';
import {LegacyAddress, LegacyDelivery, LegacyOrder, legacyOrderCommands, useLocalization} from '@hantera/portal-app';
import {ToggleButton, useClose} from '@hantera/design-system';

const props = defineProps<{
  order: LegacyOrder
  address: LegacyAddress
  deliveries: LegacyDelivery[]
  isInvoiceAddress: boolean
}>();

const close = useClose();
const addressEditor = ref<typeof AddressEditor>();
const backend = useBackend();
const t = useLocalization();
const assignTo = ref<{
  delivery: LegacyDelivery
  assign: any
}[]>([]);
const editingAddress = ref(props.address);
const assignToInvoiceAddress = ref(props.isInvoiceAddress);

const assignCount = computed(() => {
  const deliveryCount = assignTo.value.reduce((res, a) => a.assign ? res + 1 : res, 0);

  if (assignToInvoiceAddress.value) {
    return deliveryCount + 1;
  } else {
    return deliveryCount;
  }
});
const disableSave = computed(() => assignCount.value === 0);

onMounted(() => {
  assignTo.value = props.order.deliveries.map(d => {

    let assignValue: boolean | ComputedRef<boolean> = props.deliveries.findIndex(r => r.deliveryId === d.deliveryId) >= 0;
    // if (d.state !== 'Open') {
    //   assignValue = computed(() => objectsAreEqual(editingAddress.value, props.address));
    // }

    return {
      delivery: d,
      assign: assignValue
    };
  });
});

async function save() {
  if (!addressEditor.value || !addressEditor.value.validate()) {
    return;
  }

  const assignToDeliveryNumbers = assignTo.value.filter(r => r.assign).map(r => r.delivery.deliveryNumber);
  const messageParts: string[] = [];
  if (assignToInvoiceAddress.value) {
    messageParts.push('invoice address');
  }
  if (assignToDeliveryNumbers.length > 0) {
    messageParts.push('address of deliveries: {deliveryNumbers}');
  }

  const builder = backend.legacyOrders.buildCommandGroup(
    props.order.orderId,
    `Set ${messageParts.join(' and ')}`,
    {
      deliveryNumbers: assignTo.value.filter(r => r.assign).map(r => r.delivery.deliveryNumber).join(', '),
    }
  );

  assignTo.value.filter(r => r.assign).map(r => r.delivery).forEach(d => {
    builder.command(legacyOrderCommands.setDeliveryAddress, {
      deliveryId: d.deliveryId,
      address: editingAddress.value
    });
  });

  if (assignToInvoiceAddress.value) {
    builder.command(legacyOrderCommands.setBillingAddress, {
      address: editingAddress.value
    });
  }

  builder.command(legacyOrderCommands.addActivityLog, {
    message: `Set ${messageParts.join(' and ')}`,
    fields: {
      deliveryNumbers: assignTo.value.filter(r => r.assign).map(r => r.delivery.deliveryNumber).join(', '),
    }
  });

  await builder.send();
  close();
}

function objectsAreEqual(a: any, b: any): boolean {
  const keysA = Object.keys(a);
  const keysB = Object.keys(b);

  if (keysA.length !== keysB.length) {
    return false;
  }

  for (const key of keysA) {
    if (a[key] !== b[key]) {
      return false;
    }
  }

  return true;
}

</script>

<template>
  <div class="order-address__editor-container">
    <AddressEditor
      v-model="editingAddress"
      ref="addressEditor"
    />

    <div>
      <label>{{ t('orderView.assignDeliveryAddressTo', {
        count: assignCount
      }) }}</label>
      <div class="order-address__editor-deliveries neat-scroll">
        <ToggleButton
          class="order-address__editor-delivery"
          v-model="assignToInvoiceAddress"
        >
          {{ t('orderView.invoiceAddress') }}
        </ToggleButton>
        <ToggleButton
          class="order-address__editor-delivery"
          v-for="assign in assignTo"
          :key="assign.delivery.deliveryId"
          v-model="assign.assign"
        >
          {{ assign.delivery.deliveryNumber ?? t('orderView.delivery.newDelivery') }}
        </ToggleButton>
      </div>
    </div>
  </div>

  <div class="order-address__editor-actions">
    <button
      class="btn-primary"
      :disabled="disableSave"
      @click="save"
    >{{ t('common.save') }}</button>
  </div>
</template>

<style scoped>
.order-address__editor-container {
  display: grid;
  gap: 10px;
  grid-template-columns: minmax(300px, 320px) minmax(200px, max-content);
}

@media (max-width: 768px) {
  .order-address__editor-container {
    display: flex;
    flex-direction: column;
  }
}

.order-address__editor-deliveries {
  display: grid;
  grid-auto-flow: row;
  grid-auto-rows: min-content;
  gap: 5px;
  max-height: 200px;
  overflow: hidden;
  padding-bottom: 5px;
}

.order-address__editor-actions {
  margin-top: 10px;
  display: flex;
  justify-content: flex-end;
}
</style>